import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
} from "recharts";
import { Loader2, TrendingUp, Award, Clock, Scale, LineChart as LineIcon } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function Reports() {
  const [tab, setTab] = useState("by-supplier");
  const [bySupplier, setBySupplier] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [priceComp, setPriceComp] = useState(null);
  const [aging, setAging] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [a, b, c, d, e] = await Promise.all([
          api.get("/reports/purchases-by-supplier"),
          api.get("/reports/monthly-purchasing"),
          api.get("/reports/supplier-performance"),
          api.get("/reports/price-comparison"),
          api.get("/reports/stock-aging"),
        ]);
        setBySupplier(a.data.rows);
        setMonthly(b.data.rows);
        setPerformance(c.data.rows);
        setPriceComp(d.data.rows);
        setAging(e.data.rows);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div>
      <PageHeader
        title="Raporlar"
        subtitle="Tedarikçi, satın alma ve stok analizi"
        breadcrumbs={["Modüller", "Raporlar"]}
      />

      <div className="p-6 lg:p-8">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
          </div>
        ) : (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-slate-900 border border-slate-800 rounded-sm h-auto p-1 mb-5 flex-wrap justify-start">
              <TabsTrigger data-testid="tab-by-supplier" value="by-supplier" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-sm text-xs uppercase tracking-wider font-semibold px-4 py-2">
                <TrendingUp className="w-3.5 h-3.5 mr-2" /> Tedarikçi Alımları
              </TabsTrigger>
              <TabsTrigger data-testid="tab-monthly" value="monthly" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-sm text-xs uppercase tracking-wider font-semibold px-4 py-2">
                <LineIcon className="w-3.5 h-3.5 mr-2" /> Aylık Trend
              </TabsTrigger>
              <TabsTrigger data-testid="tab-performance" value="performance" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-sm text-xs uppercase tracking-wider font-semibold px-4 py-2">
                <Award className="w-3.5 h-3.5 mr-2" /> Performans
              </TabsTrigger>
              <TabsTrigger data-testid="tab-price" value="price" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-sm text-xs uppercase tracking-wider font-semibold px-4 py-2">
                <Scale className="w-3.5 h-3.5 mr-2" /> Fiyat Karşılaştırma
              </TabsTrigger>
              <TabsTrigger data-testid="tab-aging" value="aging" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-sm text-xs uppercase tracking-wider font-semibold px-4 py-2">
                <Clock className="w-3.5 h-3.5 mr-2" /> Stok Yaşlanma
              </TabsTrigger>
            </TabsList>

            {/* ------ By Supplier ------ */}
            <TabsContent value="by-supplier">
              <Card className="bg-slate-900 border-slate-800 rounded-sm p-6 mb-4">
                <h3 className="text-sm font-bold text-slate-50 tracking-tight mb-4 uppercase font-mono">Tedarikçi Bazında Toplam Harcama (₺)</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={bySupplier} margin={{ left: 0, right: 8, top: 8, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                      <XAxis dataKey="supplier_name" stroke="#64748b" tick={{ fontSize: 10, fill: "#94a3b8" }} angle={-30} textAnchor="end" height={60} interval={0} />
                      <YAxis stroke="#64748b" tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                      <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 2, fontSize: 12 }} cursor={{ fill: "rgba(234, 88, 12, 0.08)" }} formatter={(v) => `₺${fmt(v)}`} />
                      <Bar dataKey="total_spent" radius={[2, 2, 0, 0]}>
                        {bySupplier.map((_, i) => <Cell key={i} fill="#2563eb" />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
              <Card className="bg-slate-900 border-slate-800 rounded-sm overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent border-slate-800">
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Tedarikçi</TableHead>
                      <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Alım Adedi</TableHead>
                      <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Farklı Malzeme</TableHead>
                      <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Toplam ₺</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bySupplier.map((r) => (
                      <TableRow key={r.supplier_id} className="border-slate-800 hover:bg-slate-800/40">
                        <TableCell className="text-slate-100 font-medium">{r.supplier_name}</TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-slate-300">{r.purchase_count}</TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-slate-300">{r.unique_materials}</TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-blue-500 font-semibold">₺{fmt(r.total_spent)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </TabsContent>

            {/* ------ Monthly ------ */}
            <TabsContent value="monthly">
              <Card className="bg-slate-900 border-slate-800 rounded-sm p-6 mb-4">
                <h3 className="text-sm font-bold text-slate-50 tracking-tight mb-4 uppercase font-mono">Aylık Satın Alma Trendi</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={monthly} margin={{ left: 0, right: 8, top: 8, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                      <XAxis dataKey="month" stroke="#64748b" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                      <YAxis stroke="#64748b" tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                      <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 2, fontSize: 12 }} formatter={(v) => `₺${fmt(v)}`} />
                      <Line type="monotone" dataKey="total" stroke="#2563eb" strokeWidth={2.5} dot={{ fill: "#2563eb", r: 4 }} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Card>
              <Card className="bg-slate-900 border-slate-800 rounded-sm overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent border-slate-800">
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Ay</TableHead>
                      <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Alım Adedi</TableHead>
                      <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Toplam ₺</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {monthly.map((r) => (
                      <TableRow key={r.month} className="border-slate-800 hover:bg-slate-800/40">
                        <TableCell className="font-mono text-slate-100">{r.month}</TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-slate-300">{r.count}</TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-blue-500 font-semibold">₺{fmt(r.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </TabsContent>

            {/* ------ Performance ------ */}
            <TabsContent value="performance">
              <Card className="bg-slate-900 border-slate-800 rounded-sm overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent border-slate-800">
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Tedarikçi</TableHead>
                      <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Alım</TableHead>
                      <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Miktar</TableHead>
                      <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Harcama ₺</TableHead>
                      <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Zamanında %</TableHead>
                      <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Son Alım</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {performance.map((r) => (
                      <TableRow key={r.supplier_id} className="border-slate-800 hover:bg-slate-800/40">
                        <TableCell className="text-slate-100 font-medium">{r.supplier_name}</TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-slate-300">{r.purchase_count}</TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-slate-300">{fmt(r.total_quantity)}</TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-slate-100 font-semibold">₺{fmt(r.total_spent)}</TableCell>
                        <TableCell className="text-right">
                          <span className={`font-mono tabular-nums text-xs px-1.5 py-0.5 rounded-sm ${
                            r.on_time_rate >= 90 ? "text-emerald-400 bg-emerald-950/40"
                            : r.on_time_rate >= 70 ? "text-yellow-400 bg-yellow-950/40"
                            : "text-red-400 bg-red-950/40"
                          }`}>
                            {r.on_time_rate.toFixed(1)}%
                          </span>
                        </TableCell>
                        <TableCell className="text-right text-xs font-mono text-slate-500">
                          {r.days_since_last !== null ? `${r.days_since_last} gün önce` : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </TabsContent>

            {/* ------ Price Comparison ------ */}
            <TabsContent value="price">
              {priceComp.length === 0 ? (
                <Card className="bg-slate-900 border-slate-800 rounded-sm p-10 text-center">
                  <div className="text-slate-400 text-sm">
                    Fiyat karşılaştırması için aynı malzemeyi en az 2 tedarikçiden almış olmanız gerekiyor.
                  </div>
                </Card>
              ) : (
                <div className="space-y-4">
                  {priceComp.map((m) => (
                    <Card key={`${m.material_type}-${m.material_id}`} className="bg-slate-900 border-slate-800 rounded-sm p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="text-[10px] font-mono uppercase tracking-widest text-blue-500">{m.material_code}</div>
                          <div className="text-base font-bold text-slate-50 tracking-tight">{m.material_name}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Tasarruf Potansiyeli</div>
                          <div className="text-lg font-bold text-emerald-400 tabular-nums">{m.savings_pct}%</div>
                        </div>
                      </div>
                      <Table>
                        <TableHeader>
                          <TableRow className="hover:bg-transparent border-slate-800">
                            <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Tedarikçi</TableHead>
                            <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Ort. Fiyat</TableHead>
                            <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Son Fiyat</TableHead>
                            <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Toplam Miktar</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {m.suppliers.map((s, i) => (
                            <TableRow key={s.supplier_id} className="border-slate-800 hover:bg-slate-800/40">
                              <TableCell className="text-slate-100">
                                <span className="inline-flex items-center gap-2">
                                  {i === 0 && <span className="text-[9px] font-mono uppercase tracking-widest text-emerald-400 bg-emerald-950/40 px-1.5 py-0.5 rounded-sm">En İyi</span>}
                                  {s.supplier_name}
                                </span>
                              </TableCell>
                              <TableCell className={`text-right font-mono tabular-nums ${i === 0 ? "text-emerald-400 font-semibold" : "text-slate-300"}`}>₺{fmt(s.avg_price)}</TableCell>
                              <TableCell className="text-right font-mono tabular-nums text-slate-400">₺{fmt(s.last_price)}</TableCell>
                              <TableCell className="text-right font-mono tabular-nums text-slate-400">{fmt(s.total_qty)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* ------ Stock Aging ------ */}
            <TabsContent value="aging">
              <Card className="bg-slate-900 border-slate-800 rounded-sm overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent border-slate-800">
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Kod</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Malzeme</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Tip</TableHead>
                      <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Stok</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Son Alım</TableHead>
                      <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Yaş (gün)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {aging.map((r) => (
                      <TableRow key={`${r.material_type}-${r.material_id}`} className="border-slate-800 hover:bg-slate-800/40">
                        <TableCell className="font-mono text-xs text-blue-500 font-semibold">{r.code}</TableCell>
                        <TableCell className="text-slate-100">{r.name}</TableCell>
                        <TableCell className="text-slate-400 text-xs">{r.material_type === "fabric" ? "Kumaş" : "Aksesuar"}</TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-slate-300">{fmt(r.stock)} <span className="text-slate-500 text-[10px]">{r.unit}</span></TableCell>
                        <TableCell className="font-mono text-xs text-slate-400">{r.last_purchase_date ? new Date(r.last_purchase_date).toLocaleDateString("tr-TR") : "—"}</TableCell>
                        <TableCell className="text-right">
                          {r.days_since === null ? (
                            <span className="text-slate-600 text-xs font-mono">—</span>
                          ) : (
                            <span className={`font-mono tabular-nums text-xs px-1.5 py-0.5 rounded-sm ${
                              r.days_since > 180 ? "text-red-400 bg-red-950/40"
                              : r.days_since > 90 ? "text-yellow-400 bg-yellow-950/40"
                              : "text-emerald-400 bg-emerald-950/40"
                            }`}>
                              {r.days_since}g
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
